// ===========================================
// Add to Calendar buttons (Google link + .ics)
// ===========================================
//
// Sharing is a button the family chooses, never automatic. The Google link is built from the
// REAL instants (`startsAtIso` / `endsAtIso`), formatted as UTC by `googleCalendarEventUrl` —
// never from a calendar item's reconstructed fake-local `start`, which is a wall-clock Date.
// Rendered only when `available`: the by-id .ics route serves PUBLISHED events only, so a
// cancelled or finished one gets nothing (pass `status === 'PUBLISHED'`, never `!cancelled`).

import { Box, Button } from '@mui/material';
import CalendarIcon from '@mui/icons-material/CalendarToday';
import ShareIcon from '@mui/icons-material/Share';
import { eventIcsByIdUrl, googleCalendarEventUrl } from '../../api/events.api';
import { useTenant } from '../../contexts/TenantContext';

export interface AddToCalendarButtonsProps {
  eventId: string;
  title: string;
  /** Real instant, ISO string. */
  startsAtIso: string;
  endsAtIso?: string | null;
  details?: string | null;
  location?: string | null;
  /** True only for a PUBLISHED event. */
  available: boolean;
}

export default function AddToCalendarButtons({
  eventId,
  title,
  startsAtIso,
  endsAtIso,
  details,
  location,
  available,
}: AddToCalendarButtonsProps) {
  const { schoolSlug } = useTenant();
  if (!available) return null;

  const googleUrl = googleCalendarEventUrl({
    title,
    start: startsAtIso,
    end: endsAtIso,
    details,
    location,
  });
  const icsUrl = eventIcsByIdUrl(schoolSlug || '', eventId);

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
      <Button
        component="a"
        href={googleUrl}
        target="_blank"
        rel="noopener noreferrer"
        variant="outlined"
        size="small"
        startIcon={<CalendarIcon />}
      >
        Google Calendar
      </Button>
      <Button
        component="a"
        href={icsUrl}
        target="_blank"
        rel="noopener noreferrer"
        variant="outlined"
        size="small"
        startIcon={<ShareIcon />}
      >
        Apple / Outlook (.ics)
      </Button>
    </Box>
  );
}
